import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { logError } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingContext = createContext(null);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

export const OnboardingProvider = ({ children }) => {
  const { user, getAuthHeaders } = useAuth();
  
  const [loading, setLoading]                 = useState(true);
  const [showProfileSetup, setShowProfileSetup] = useState(false);
  const [showChecklist, setShowChecklist]     = useState(false);
  const [onboardingData, setOnboardingData]   = useState(null);
  const [workflow, setWorkflow]               = useState([]);
  const [progressPercentage, setProgressPercentage] = useState(0);

  // WS-3.1: Optimistic update cache — holds local step-completion state so we
  // don't need to re-fetch the full progress after every micro-mutation.
  const optimisticRef = useRef({});

  // Fetch onboarding status
  const fetchOnboardingStatus = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API}/user/onboarding`, { withCredentials: true });
      const data = response.data;
      const phase = data.progress?.phase;

      setOnboardingData(data);
      setWorkflow(data.workflow || []);
      setProgressPercentage(data.progress_percentage || 0);

      if (phase === 'profile_setup') {
        setShowProfileSetup(!data.snoozed);
      } else {
        setShowProfileSetup(false);
        if (phase === 'completed') setShowChecklist(false);
      }
    } catch (error) {
      // WS-2.3: surface fetch failures — but only once and only for real errors
      if (error.response?.status >= 500) {
        logError(error, { context: 'OnboardingContext.fetchOnboardingStatus' });
        // Don't toast on 500s during initial load — it's noise
      }
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders]);

  useEffect(() => {
    fetchOnboardingStatus();
  }, [fetchOnboardingStatus]);

  // WS-3.1: Apply optimistic update locally before firing the API call.
  // Returns a rollback function that callers can use on failure.
  const _applyOptimistic = (stepId, substepId) => {
    const key = substepId ? `${stepId}.${substepId}` : stepId;
    const prev = optimisticRef.current[key];
    optimisticRef.current[key] = 'completed';

    // Bump local progress percentage optimistically (+5 per action, capped at 100)
    setProgressPercentage(p => Math.min(100, p + 5));

    return () => {
      // Rollback
      if (prev === undefined) delete optimisticRef.current[key];
      else optimisticRef.current[key] = prev;
      setProgressPercentage(p => Math.max(0, p - 5));
    };
  };

  // Complete a substep
  // WS-3.1: Optimistic — updates local state immediately, refreshes in background
  const completeSubstep = async (stepId, substepId) => {
    const rollback = _applyOptimistic(stepId, substepId);
    try {
      await axios.post(
        `${API}/user/onboarding/complete-substep/${stepId}/${substepId}`,
        {},
        { withCredentials: true }
      );
      // WS-3.1: Background refresh without blocking the caller
      fetchOnboardingStatus().catch(() => {});
    } catch (error) {
      rollback();
      // WS-2.3: Surface errors to the user
      logError(error, { context: 'OnboardingContext.completeSubstep', stepId, substepId });
      if (error.response?.status >= 400) {
        toast.error('Could not save progress. Please try again.');
      }
    }
  };

  // Complete entire step — WS-3.1: optimistic + background refresh
  const completeStep = async (stepId) => {
    const rollback = _applyOptimistic(stepId, null);
    try {
      await axios.post(
        `${API}/user/onboarding/complete-step/${stepId}`,
        {},
        { withCredentials: true }
      );
      fetchOnboardingStatus().catch(() => {});
    } catch (error) {
      rollback();
      logError(error, { context: 'OnboardingContext.completeStep', stepId });
      if (error.response?.status >= 400) {
        toast.error('Could not save step progress. Please try again.');
      }
    }
  };

  // Update current position — WS-3.1: fire-and-forget (no re-fetch, non-blocking)
  const updateProgress = async (currentStep, currentSubstep) => {
    try {
      await axios.put(
        `${API}/user/onboarding`,
        { current_step: currentStep, current_substep: currentSubstep },
        { withCredentials: true }
      );
      // WS-3.1: No fetchOnboardingStatus() here — position updates are metadata
    } catch (error) {
      logError(error, { context: 'OnboardingContext.updateProgress' });
      // Silently fail — position update is non-critical
    }
  };

  // Skip onboarding
  const skipOnboarding = async (neverShow = false) => {
    try {
      await axios.post(
        `${API}/user/onboarding/skip`,
        { skip_type: neverShow ? 'never_show' : 'remind_later' },
        { withCredentials: true }
      );
      setShowProfileSetup(false);
      setShowChecklist(false);
      fetchOnboardingStatus().catch(() => {});
    } catch (error) {
      logError(error, { context: 'OnboardingContext.skipOnboarding' });
      // WS-2.3: Surface skip failure
      toast.error('Could not skip onboarding. Please try again.');
    }
  };

  // Reset onboarding
  const resetOnboarding = async () => {
    try {
      await axios.post(
        `${API}/user/onboarding/reset`,
        {},
        { withCredentials: true }
      );
      optimisticRef.current = {};
      await fetchOnboardingStatus();
    } catch (error) {
      logError(error, { context: 'OnboardingContext.resetOnboarding' });
      toast.error('Could not reset onboarding.');
    }
  };

  // Modal already persisted the skip/snooze itself — just close and refresh
  const closeProfileSetup = () => {
    setShowProfileSetup(false);
    fetchOnboardingStatus().catch(() => {});
  };

  // Profile setup completed
  const onProfileSetupComplete = async () => {
    setShowProfileSetup(false);
    setShowChecklist(true);
    await fetchOnboardingStatus();
  };

  // Toggle checklist visibility
  const toggleChecklist = () => {
    setShowChecklist(prev => !prev);
  };

  // Expose the optimistic completion map so UI can reflect it instantly
  const getOptimisticStatus = (stepId, substepId) => {
    const key = substepId ? `${stepId}.${substepId}` : stepId;
    return optimisticRef.current[key] || null;
  };

  // WS-3.1: triggerAction maps UI interaction names to onboarding substep completions.
  // Restored after being accidentally removed in the optimistic-ref refactor.
  const TRIGGER_ACTION_MAP = {
    'view_dashboard':        { stepId: 'dashboard',       substepId: 'tax_health' },
    'view_deductions':       { stepId: 'deductions',      substepId: 'review_auto' },
    'add_deduction':         { stepId: 'deductions',      substepId: 'add_manual' },
    'upload_document':       { stepId: 'documents',       substepId: 'upload_first' },
    'categorize_document':   { stepId: 'documents',       substepId: 'categorize' },
    'connect_bank':          { stepId: 'bank_connections',substepId: 'connect' },
    'view_quarterly':        { stepId: 'quarterly',       substepId: 'view_breakdown' },
    'generate_report':       { stepId: 'reports',         substepId: 'generate_reports' },
    'create_tax_package':    { stepId: 'reports',         substepId: 'tax_package' },
    'send_chat_message':     { stepId: 'ai_advisor',      substepId: 'first_question' },
    'ask_strategy_question': { stepId: 'ai_advisor',      substepId: 'explore_strategies' },
  };

  const triggerAction = async (actionName) => {
    const mapping = TRIGGER_ACTION_MAP[actionName];
    if (!mapping) return; // unknown action — no-op
    const { stepId, substepId } = mapping;
    // Skip if already completed (optimistic or server-confirmed)
    if (isSubstepCompleted(stepId, substepId)) return;
    await completeSubstep(stepId, substepId);
  };
  // Restored so consumers (OnboardingChecklist) continue to work after the
  // optimistic-ref refactor.
  const isSubstepCompleted = (stepId, substepId) => {
    const key = `${stepId}.${substepId}`;
    if (getOptimisticStatus(stepId, substepId) === 'completed') return true;
    const completed = onboardingData?.progress?.completed_steps || [];
    return completed.includes(key);
  };

  const isStepCompleted = (stepId) => {
    if (getOptimisticStatus(stepId, null) === 'completed') return true;
    const completed = onboardingData?.progress?.completed_steps || [];
    return completed.includes(stepId);
  };

  const getStepProgress = (stepId) => {
    const step = (workflow || []).find(s => s.id === stepId);
    if (!step || !step.substeps) return { completed: 0, total: 0 };
    const total = step.substeps.length;
    const completedCount = step.substeps.filter(sub => isSubstepCompleted(stepId, sub.id)).length;
    return { completed: completedCount, total };
  };

  return (
    <OnboardingContext.Provider value={{
      loading,
      showProfileSetup,
      showChecklist,
      setShowChecklist,
      onboardingData,
      closeProfileSetup,
      workflow,
      progressPercentage,
      fetchOnboardingStatus,
      completeSubstep,
      completeStep,
      updateProgress,
      skipOnboarding,
      resetOnboarding,
      onProfileSetupComplete,
      toggleChecklist,
      getOptimisticStatus,   // WS-3.1: optimistic-ref accessor
      triggerAction,         // WS-3.1: restored action-to-substep mapper
      isStepCompleted,       // WS-3.1: restored helper
      isSubstepCompleted,    // WS-3.1: restored helper
      getStepProgress,       // WS-3.1: restored helper
    }}>
      {children}
    </OnboardingContext.Provider>
  );
};
